/**
 * §7.20 Media Coverage — Phase 5 CD-spec implementation.
 *
 * One surface that demonstrates all four CD-spec R1–R4 deliverables together:
 *   - R3 quadrant up top (cross-cutting: severity × coverage)
 *   - R1 + R4 in the bottom-left (per-outbreak Media Coverage section,
 *     with a clickable badge that drives the side-pane)
 *   - R2 in the bottom-right (source drill-down side-pane)
 *
 * Data is illustrative (`coverage/sample-data.ts`) and clearly marked.
 * PaCC does not yet ingest media — wire the real pipeline (ProMED · WHO
 * sitreps · GDELT) when the Phase 5 data sub-track lands.
 *
 * R5 (map coverage-gap colour-mode) is deferred per CD's recommendation:
 * "after the badge + drill-down prove the data".
 */

import { useState } from 'react';
import { SAMPLE_COVERAGE, SAMPLE_ARTICLES } from '../coverage/sample-data';
import { MediaCoverageSection } from '../coverage/MediaCoverageSection';
import { SourcePane } from '../coverage/SourcePane';
import { CoverageQuadrant } from '../coverage/CoverageQuadrant';
import type { OutbreakCoverage, CoverageArticle } from '../coverage/types';

// Prefer the live coverage snapshot (`coverage.snapshot.json`, written by
// `npm run ingest:coverage`) when present; fall back to the illustrative
// sample-data otherwise. The PaCC App.tsx pattern (FEED_IS_LIVE) checks for
// snapshot presence in catalog.ts; we do the equivalent here with a static
// import that resolves to null when the file isn't on disk.
type LiveSnapshot = {
  generatedAt: string;
  source: 'gdelt';
  outbreaks: Array<OutbreakCoverage & {
    common: string; family: string; admin: string; iso3: string; threatTier: 0 | 1 | 2 | 3 | 4;
  }>;
  articles: CoverageArticle[];
};

// Build-safe optional load. The coverage snapshot is gitignored (written only
// by `npm run ingest:coverage`), so it is frequently ABSENT at build time. A
// top-level `await import('../coverage.snapshot.json')` makes Rollup HARD-FAIL
// the production build when the file is missing (the try/catch only guards the
// runtime, not bundling). `import.meta.glob` is the build-safe equivalent: it
// returns `{}` when the optional file is absent and the bundle still builds —
// the same pattern catalog.ts uses for signals.snapshot.json.
const coverageSnapMods = import.meta.glob<{ default: LiveSnapshot }>(
  '../coverage.snapshot.json',
  { eager: true },
);
const LIVE_SNAPSHOT: LiveSnapshot | null =
  Object.values(coverageSnapMods)[0]?.default ?? null;

const COVERAGE = (LIVE_SNAPSHOT?.outbreaks ?? SAMPLE_COVERAGE) as typeof SAMPLE_COVERAGE;
const ARTICLES = LIVE_SNAPSHOT?.articles ?? SAMPLE_ARTICLES;
const IS_LIVE = LIVE_SNAPSHOT !== null;

export function MediaCoverage() {
  // The outbreak the per-outbreak detail panel is showing. Default: the
  // CD-spec focal outbreak (Ebola DRC) — the high-severity, low-coverage
  // case that motivates the whole layer.
  const [selectedId, setSelectedId] = useState<string>('ob-ebola-drc');
  const [paneOpen, setPaneOpen] = useState(false);

  const selected = COVERAGE.find(o => o.outbreakId === selectedId) ?? COVERAGE[0]!;
  const articles = ARTICLES.filter(a => a.outbreakId === selectedId);

  const points = COVERAGE.map(o => ({
    outbreakId: o.outbreakId,
    common: o.common,
    admin: o.admin,
    threatTier: o.threatTier,
    coverageLevel: o.windows.d7.level,
    asymmetryKind: o.asymmetry?.kind ?? null,
  }));

  return (
    <div className="surface">
      <div className="surface__header">
        <span className="surface__sec">§7.20</span>
        <h1 className="surface__title">Media Coverage</h1>
      </div>
      <div className="surface__sub">
        Coverage volume + sources per outbreak, with severity × coverage cross-cut.
        {IS_LIVE ? (
          <>
            {' '}<strong style={{ color: 'var(--bm-cov)' }}>LIVE</strong> · GDELT Doc 2.0 ·
            snapshot {LIVE_SNAPSHOT?.generatedAt.slice(0, 16).replace('T', ' ')} UTC.
            Run <code>npm run ingest:coverage</code> in <code>apps/pacc-prototype</code>
            to refresh.
          </>
        ) : (
          <>
            {' '}ILLUSTRATIVE sample data — PaCC does not yet ingest media. Run
            <code> npm run ingest:coverage</code> in <code>apps/pacc-prototype</code>
            (requires NODE_OPTIONS=--use-system-ca) to hydrate from GDELT.
          </>
        )}
      </div>

      {/* R3 — quadrant: the cross-cutting "what's under the radar?" view */}
      <section style={{ marginTop: 16 }}>
        <h2 style={{
          fontFamily: 'var(--bm-font-mono)', fontSize: 12, fontWeight: 600,
          letterSpacing: '.5px', textTransform: 'uppercase',
          color: 'var(--bm-text-muted)', margin: '0 0 8px',
        }}>Severity × coverage — what's under the radar?</h2>
        <p style={{
          margin: '0 0 14px', fontSize: 13, lineHeight: 1.55,
          color: 'var(--bm-text-subtle)', maxWidth: 700,
        }}>
          Y axis is threat severity (CRITICAL at top); X axis is coverage volume
          (Silent → High). The top-left zone is the operational priority — high
          severity with low coverage. Dashed ring = under-reported. Hover any
          point for details.
        </p>
        <CoverageQuadrant points={points} />
      </section>

      {/* R1 + R4 + R2 side by side */}
      <section style={{ marginTop: 24 }}>
        <h2 style={{
          fontFamily: 'var(--bm-font-mono)', fontSize: 12, fontWeight: 600,
          letterSpacing: '.5px', textTransform: 'uppercase',
          color: 'var(--bm-text-muted)', margin: '0 0 8px',
        }}>Per-outbreak coverage — pick an outbreak</h2>

        <div style={{
          display: 'flex', flexWrap: 'wrap', gap: 6, margin: '0 0 14px',
        }}>
          {SAMPLE_COVERAGE.map(o => (
            <button
              key={o.outbreakId}
              type="button"
              onClick={() => { setSelectedId(o.outbreakId); setPaneOpen(false); }}
              style={{
                padding: '4px 10px', borderRadius: 999,
                fontFamily: 'var(--bm-font-mono)', fontSize: 11, fontWeight: 600,
                border: `1px solid ${selectedId === o.outbreakId ? 'var(--bm-text-muted)' : 'var(--bm-border)'}`,
                background: selectedId === o.outbreakId ? 'var(--bm-bg-subtle)' : 'transparent',
                color: selectedId === o.outbreakId ? 'var(--bm-text)' : 'var(--bm-text-subtle)',
                cursor: 'pointer',
              }}
            >{o.common}{o.noData ? ' (no data)' : ''}</button>
          ))}
        </div>

        <div style={{
          display: 'grid', gridTemplateColumns: 'minmax(0, 1fr) auto',
          gap: 20, alignItems: 'start',
        }}>
          {/* Left: synthetic "outbreak detail panel" that hosts the
              MediaCoverageSection (R1) or CoverageEmpty (R4). */}
          <div className="panel" style={{ padding: 14 }}>
            <header style={{ marginBottom: 8 }}>
              <div style={{ fontWeight: 700, fontSize: 17, color: 'var(--bm-text)' }}>{selected.common}</div>
              <div style={{
                fontFamily: 'var(--bm-font-mono)', fontSize: 11.5, color: 'var(--bm-text-subtle)',
              }}>{selected.admin} · ISO3 {selected.iso3} · threat tier {selected.threatTier}</div>
            </header>
            <MediaCoverageSection
              coverage={selected}
              threatTier={selected.threatTier}
              onShowSources={selected.noData ? undefined : () => setPaneOpen(true)}
            />
          </div>

          {/* Right: source drill-down side-pane (R2), only when open + the
              selected outbreak has articles. Stays mounted alongside the
              detail panel — doesn't dismiss it. */}
          {paneOpen && articles.length > 0 && (
            <SourcePane
              outbreakLabel={selected.common}
              articles={articles}
              onClose={() => setPaneOpen(false)}
            />
          )}
        </div>
      </section>
    </div>
  );
}
